/**
 * ChronicleLocationBlock — DEV-1336 (note 6, Pass 2).
 *
 * "Where it happened" on the chronicle About: a pinned-location mini-map plus
 * the local conditions (entity_context) as chips. Shown for ALL chronicles
 * (native or attributed). This is now the single home for local conditions —
 * the attributed block no longer renders them (DEV-1321: conditions live on About).
 */

import LocationPickerMap from '../ProjectStepper/LocationPickerMap';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ChronicleResponse } from '../../api/chronicles';

const CTX_LABEL_KEY: Record<string, string> = {
  region: 'ctxRegion',
  climate: 'ctxClimate',
  urban_rural: 'ctxUrbanRural',
  scale: 'ctxScale',
  demographics: 'ctxDemographics',
  other: 'ctxOther',
};

interface Props {
  chronicle: ChronicleResponse;
}

export default function ChronicleLocationBlock({ chronicle }: Props) {
  const { t } = useAppTranslation('pages');

  const hasCoords = chronicle.locationLat != null && chronicle.locationLon != null;
  const place = chronicle.locationPlaceText?.trim();
  const contexts = chronicle.contexts ?? [];

  if (!hasCoords && !place && contexts.length === 0) return null;

  return (
    <section>
      <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>
        {t('chronicleSettings.whereLabel')}
      </h2>

      {place && (
        <div style={{ color: 'var(--text-secondary)', marginBlockEnd: hasCoords ? 10 : 0 }}>{place}</div>
      )}

      {hasCoords && (
        <LocationPickerMap lat={String(chronicle.locationLat)} lon={String(chronicle.locationLon)} readOnly />
      )}

      {contexts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBlockStart: hasCoords || place ? 12 : 0 }}>
          {contexts.map((ctx) => (
            <span
              key={ctx.contextGuid}
              style={{
                display: 'inline-flex', alignItems: 'baseline', gap: 6,
                fontSize: 12.5, padding: '5px 11px', borderRadius: 999,
                border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-secondary)',
              }}
            >
              <span style={{ color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: 10.5, letterSpacing: '0.03em' }}>
                {t(`chronicleSettings.${CTX_LABEL_KEY[ctx.contextKey] ?? 'ctxOther'}`)}
              </span>
              {ctx.contextValue}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
